import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createBrand,
  fetchAllBrands,
  updateBrand,
  fetchOneBrand,
  deleteBrand,
  fetchBrandTree,
} from "services/brand";

// Fetch all brands with optional filtering
export const useAllBrands = (filters = {}, enabled = true) => {
  return useQuery({
    queryKey: ["brands", filters],
    queryFn: () => fetchAllBrands(filters),
    enabled,
  });
};

// Fetch all brands with optional filtering
export const useAllSubBrands = (parentid, filters, enabled = true) => {
  return useQuery({
    queryKey: ["subbrands", parentid, filters],
    queryFn: () => fetchAllBrands({ parentid, ...filters }),
    enabled,
  });
};

// Fetch single brand by ID
export const useOneBrand = (id, enabled = true) => {
  return useQuery({
    queryKey: ["brand", id],
    queryFn: () => fetchOneBrand(id),
    enabled: Boolean(id) && enabled,
  });
};

// Create new brand
export const useCreateBrand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => createBrand(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      queryClient.invalidateQueries({ queryKey: ["subbrands"] });
      queryClient.invalidateQueries({ queryKey: ["brandTree"] });
      queryClient.invalidateQueries({ queryKey: ["brands-by-vendor"] });
    },
  });
};

// Update existing brand
export const useUpdateBrand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => updateBrand(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      queryClient.invalidateQueries({ queryKey: ["subbrands"] });
      queryClient.invalidateQueries({ queryKey: ["brandTree"] });
      queryClient.invalidateQueries({ queryKey: ["brands-by-vendor"] });
      queryClient.invalidateQueries({ queryKey: ["brand", variables.id] });
    },
  });
};

// Delete brand
export const useDeleteBrand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteBrand(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      queryClient.invalidateQueries({ queryKey: ["subbrands"] });
      queryClient.invalidateQueries({ queryKey: ["brandTree"] });
      queryClient.invalidateQueries({ queryKey: ["brands-by-vendor"] });
      queryClient.invalidateQueries({ queryKey: ["brand", id] });
    },
  });
};

// Fetch brand tree with optional filtering
export const useBrandTree = (filters = {}, enabled = true) => {
  return useQuery({
    queryKey: ["brandTree", filters],
    queryFn: () => fetchBrandTree(filters),
    enabled,
  });
};
